import { useCallback, useEffect, useState } from 'react';
import FusePageSimple from '@fuse/core/FusePageSimple';
import FuseLoading from '@fuse/core/FuseLoading';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { styled } from '@mui/material/styles';
import {
	fetchContactMessages,
	setContactMessageHandled,
	type ContactMessageRow
} from '@/lib/api';

const Root = styled(FusePageSimple)(({ theme }) => ({
	'& .FusePageSimple-header': {
		backgroundColor: theme.vars.palette.background.paper,
		borderBottomWidth: 1,
		borderStyle: 'solid',
		borderColor: theme.vars.palette.divider
	}
}));

/**
 * Contact form submissions.
 *
 * The site has no published email address any more — every "contact us" link
 * goes to a form, and the messages land in `contact_messages`. Without this
 * screen they would only ever surface as a Slack ping, which is easy to miss
 * and impossible to search.
 *
 * Read-mostly: the only write is marking a message handled. There is no edit
 * and no delete on purpose — an inbox you can quietly rewrite is worse than
 * one you cannot.
 */
function ContactView() {
	const [filter, setFilter] = useState<'unhandled' | 'all'>('unhandled');
	const [rows, setRows] = useState<ContactMessageRow[] | null>(null);
	const [counts, setCounts] = useState({ total: 0, unhandled: 0 });
	const [error, setError] = useState<string | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [saving, setSaving] = useState<string | null>(null);

	const load = useCallback(async (next: 'unhandled' | 'all') => {
		try {
			const res = await fetchContactMessages(next);
			setRows(res.messages);
			setCounts({ total: res.total, unhandled: res.unhandled });
			setError(null);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		}
	}, []);

	useEffect(() => {
		void load(filter);
	}, [filter, load]);

	async function toggleHandled(row: ContactMessageRow) {
		setSaving(row.id);
		setError(null);

		try {
			await setContactMessageHandled({ id: row.id, handled: !row.handledAt });
			setToast(row.handledAt ? 'Marked unhandled' : 'Marked handled');
			// Reload rather than patching in place: under the "unhandled" filter the
			// row should disappear, and the counts in the header have to move too.
			await load(filter);
		} catch (err) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(null);
		}
	}

	if (error && !rows) {
		return (
			<Root
				header={
					<div className="p-6">
						<Typography className="text-2xl font-bold">Contact</Typography>
					</div>
				}
				content={
					<div className="p-6">
						<Alert severity="error">{error}</Alert>
					</div>
				}
			/>
		);
	}

	if (!rows) return <FuseLoading />;

	return (
		<Root
			header={
				<div className="p-6">
					<Typography className="text-2xl font-bold">Contact</Typography>
					<Typography
						color="text.secondary"
						className="text-sm"
					>
						Messages from the form at /contact — {counts.unhandled} unhandled of{' '}
						{counts.total}
					</Typography>
				</div>
			}
			content={
				<div className="p-6 flex flex-col gap-4 max-w-4xl">
					{error && <Alert severity="error">{error}</Alert>}

					<ToggleButtonGroup
						size="small"
						exclusive
						value={filter}
						onChange={(_, v) => v && setFilter(v)}
					>
						<ToggleButton value="unhandled">
							Unhandled ({counts.unhandled})
						</ToggleButton>
						<ToggleButton value="all">All ({counts.total})</ToggleButton>
					</ToggleButtonGroup>

					{rows.length === 0 && (
						<Paper className="p-5">
							<Typography color="text.secondary">
								{filter === 'unhandled'
									? 'Nothing outstanding.'
									: 'No messages yet.'}
							</Typography>
						</Paper>
					)}

					{rows.map((r) => (
						<Paper
							key={r.id}
							className="p-5"
							sx={{
								borderWidth: 1,
								borderStyle: 'solid',
								borderColor: r.handledAt ? 'divider' : 'warning.main'
							}}
						>
							<div className="flex items-start justify-between gap-4">
								<div className="min-w-0">
									<Typography className="font-semibold">{r.name}</Typography>
									{/* The only mailto in the product, and it is behind admin
									    auth — the public site deliberately publishes none. */}
									<Typography
										variant="body2"
										color="text.secondary"
									>
										<a
											href={`mailto:${r.email}?subject=Re:%20your%20message%20to%20Skipper's%20Locker`}
											className="underline"
										>
											{r.email}
										</a>
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										className="text-xs mt-1"
									>
										{new Date(r.createdAt).toLocaleString()}
									</Typography>
								</div>
								<div className="flex items-center gap-2 shrink-0">
									{!r.handledAt && (
										<Chip
											label="New"
											size="small"
											color="warning"
										/>
									)}
									<Button
										size="small"
										variant={r.handledAt ? 'outlined' : 'contained'}
										disabled={saving === r.id}
										onClick={() => toggleHandled(r)}
									>
										{r.handledAt ? 'Reopen' : 'Mark handled'}
									</Button>
								</div>
							</div>

							{/* whitespace-pre-wrap: people type line breaks and expect them
							    back. Collapsing them turns a readable message into a wall. */}
							<Typography
								variant="body2"
								className="mt-4 whitespace-pre-wrap"
							>
								{r.message}
							</Typography>
						</Paper>
					))}

					<Snackbar
						open={!!toast}
						autoHideDuration={2500}
						onClose={() => setToast(null)}
						message={toast ?? ''}
					/>
				</div>
			}
		/>
	);
}

export default ContactView;
